// =============================================================================
// MeuDriver — StudentReferrals (Batch L4)
// Dashboard de referrals para alunos.
// Rota: /aluno/indicacoes
// =============================================================================

import { useState } from 'react'
import { toast } from 'sonner'
import { Users, Copy, Check, Gift, Clock, CheckCircle2, Info, ExternalLink } from 'lucide-react'

import { useMyReferralCode, useMyReferralCredits, useMyReferrals, useReferralLink } from '@/hooks/useReferrals'
import { useSEO } from '@/hooks/useSEO'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

// ─── ReferralCodeCard ─────────────────────────────────────────────────────────

function ReferralCodeCard() {
  const { data: code, isLoading: codeLoading } = useMyReferralCode()
  const referralLink = useReferralLink()
  const [copiedCode, setCopiedCode] = useState(false)
  const [copiedLink, setCopiedLink] = useState(false)

  async function copyCode() {
    if (!code) return
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCode(true)
      toast.success('Código copiado!')
      setTimeout(() => setCopiedCode(false), 2000)
    } catch {
      toast.error('Não foi possível copiar.')
    }
  }

  async function copyLink() {
    if (!referralLink) return
    try {
      await navigator.clipboard.writeText(referralLink)
      setCopiedLink(true)
      toast.success('Link copiado!')
      setTimeout(() => setCopiedLink(false), 2000)
    } catch {
      toast.error('Não foi possível copiar.')
    }
  }

  return (
    <Card className="border-brand-100 bg-gradient-to-br from-brand-50 to-white">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Gift className="h-4 w-4 text-brand-600" />
          Seu código de indicacao
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {codeLoading ? (
          <Skeleton className="h-12 w-full rounded-xl" />
        ) : (
          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-xl border-2 border-brand-200 bg-white px-4 py-3 font-mono text-xl font-bold text-brand-700 tracking-widest text-center">
              {code ?? '—'}
            </div>
            <Button
              variant="outline"
              size="icon"
              className="shrink-0 h-12 w-12 border-brand-200"
              onClick={copyCode}
              disabled={!code}
              data-testid="copy-referral-code"
              aria-label="Copiar código"
            >
              {copiedCode ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4 text-brand-600" />
              )}
            </Button>
          </div>
        )}

        {referralLink && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-gray-500">Link direto</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500 font-mono truncate">
                {referralLink}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 text-xs"
                  onClick={copyLink}
                  data-testid="copy-referral-link"
                >
                  {copiedLink ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  Copiar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 text-xs"
                  asChild
                >
                  <a href={referralLink} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── CreditsCard ──────────────────────────────────────────────────────────────

function CreditsCard() {
  const { data: credits, isLoading } = useMyReferralCredits()

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Gift className="h-4 w-4 text-green-600" />
          Creditos acumulados
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-10 w-32" />
        ) : (
          <div className="space-y-1">
            <p className="text-3xl font-bold text-green-700">
              {formatCurrency(credits ?? 0)}
            </p>
            <p className="text-xs text-muted-foreground">
              Disponivel para usar no proximo agendamento
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── ReferralsList ────────────────────────────────────────────────────────────

function ReferralsList() {
  const { data: referrals, isLoading } = useMyReferrals()

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4 text-brand-600" />
          Pessoas indicadas
          {referrals && referrals.length > 0 && (
            <span className="ml-auto text-xs font-normal text-muted-foreground">
              {referrals.length} {referrals.length === 1 ? 'pessoa' : 'pessoas'}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        ) : !referrals || referrals.length === 0 ? (
          <div className="flex flex-col items-center py-8 gap-2 text-center text-muted-foreground">
            <Users className="h-10 w-10 opacity-20" />
            <p className="text-sm font-medium">Nenhuma indicacao ainda</p>
            <p className="text-xs opacity-70">
              Compartilhe seu codigo e ganhe creditos quando seus amigos fizerem a primeira aula.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {referrals.map((person) => (
              <div
                key={person.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {person.full_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Cadastrou em {formatDate(person.created_at)}
                  </p>
                </div>
                <Badge
                  variant={person.is_active ? 'default' : 'secondary'}
                  className="shrink-0 gap-1 text-xs"
                >
                  {person.is_active ? (
                    <><CheckCircle2 className="h-3 w-3" />Ativo</>
                  ) : (
                    <><Clock className="h-3 w-3" />Pendente</>
                  )}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── HowItWorksCard ───────────────────────────────────────────────────────────

function HowItWorksCard() {
  const steps = [
    {
      num: '1',
      title: 'Compartilhe seu codigo',
      desc: 'Envie seu codigo ou link para amigos que querem aprender a dirigir.',
    },
    {
      num: '2',
      title: 'Seu amigo se cadastra',
      desc: 'Ele usa seu codigo no cadastro e entra na plataforma como aluno.',
    },
    {
      num: '3',
      title: 'Voces dois ganham creditos',
      desc: 'Apos a primeira aula paga, ambos recebem creditos para usar em aulas futuras.',
    },
  ]

  return (
    <Card className="bg-amber-50 border-amber-100">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Info className="h-4 w-4 text-amber-600" />
          Como funciona
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {steps.map((step) => (
            <div key={step.num} className="flex gap-3">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-600 text-white text-xs font-bold">
                {step.num}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">{step.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function StudentReferrals() {
  useSEO({
    title: 'Minhas Indicacoes',
    description: 'Gerencie suas indicacoes e acompanhe seus creditos no MeuDriver.',
  })

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Users className="h-6 w-6 text-brand-600" />
          Minhas indicacoes
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Indique amigos e ganhe creditos para usar em aulas.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ReferralCodeCard />
        <CreditsCard />
      </div>

      <ReferralsList />
      <HowItWorksCard />
    </div>
  )
}
